import { TabItem } from "@corp-ais/omniark-ui";
import { isFlatEnrichmentModel } from "@app/shared/mappers/enrichment-detail.adapter";
import { IEnrichmentModel } from "@app/shared/models/enrichment.model";
import type { TabReadinessSummary } from "@app/feature/management/enrichment/services/publish/tab-readiness.types";

/** Stepper status — drives the per-step indicator color and icon. */
export type StepperStatus =
  "complete" | "in-progress" | "warning" | "error" | "empty";

export interface StepperItem {
  /** Canonical tab id (matches TabItem.id). */
  id: string;
  /** English label shown above the indicator. */
  label: string;
  /** Short Thai sub-label shown under the English label. */
  subLabelTh: string;
  /** Derived from TabItem.badge — drives indicator color/icon. */
  status: StepperStatus;
  /** Badge string/number to display next to the label (filled count, "!"
   *  marker, etc). Mirrors TabItem.badge for downstream rendering. */
  badge?: TabItem["badge"];
  /** Sequence number used as the indicator prefix (① ② ③ ...). */
  index: number;
  /** Optional per-tab readiness roll-up. When set, the stepper renders a
   *  tiered badge and a click-to-open popover; absent → stepper behaves
   *  as before. */
  readiness?: TabReadinessSummary;
  /** True when this tab carries unsaved per-section edits (per-section save
   *  cutover). Not computed here — the shell fills it in after resolving the
   *  stepper items, since dirtiness is a live, save-scope concern (
   *  `SectionDirtyStore` / `EnrichmentPriceDirtyStore`) unrelated to the pure
   *  tab/readiness algebra in this file. Drives the orange corner dot on the
   *  step indicator; absent/false → no dot. */
  dirty?: boolean;
}

export const PRICE_CONFIG_TAB = "price-config";
export const CATEGORY_TAB = "category";
export const SPECIAL_PROMOTION_TAB = "special-promotion";
export const PACKAGE_SETTINGS_TAB = "package-settings";

/**
 * Maps tab IDs that older deep links, validators and the preview still emit
 * to the canonical IDs of the tab strip.
 */
export const TAB_ALIASES: Readonly<Record<string, string>> = {
  // The read-only Overview and the old steps 1/2/3 are gone; what they
  // showed about a price lives in Content & pricing.
  overview: PRICE_CONFIG_TAB,
  "common-content": PRICE_CONFIG_TAB,
  "price-options": PRICE_CONFIG_TAB,
  "price-variants": PRICE_CONFIG_TAB,
  // The CIVR form tab is gone — its fields are inlined into the Pricing tab
  // behind an exact-CIVR channel gate.
  "civr-form": PRICE_CONFIG_TAB,
  placement: CATEGORY_TAB,
  // Term and Product attributes are one Package settings tab: the package
  // fields shared by every price.
  settings: PACKAGE_SETTINGS_TAB,
  terms: PACKAGE_SETTINGS_TAB,
  "availability-terms": PACKAGE_SETTINGS_TAB,
  "availability-and-terms": PACKAGE_SETTINGS_TAB,
  "product-attributes": PACKAGE_SETTINGS_TAB,
  "po-config": PACKAGE_SETTINGS_TAB,
  "custom-config": PACKAGE_SETTINGS_TAB,
  "package-content": PACKAGE_SETTINGS_TAB,
  "package-identity": PACKAGE_SETTINGS_TAB,
  "system-details": PACKAGE_SETTINGS_TAB,
  "external-identifiers": PACKAGE_SETTINGS_TAB,
};

/** Display labels for tabs, keyed by canonical tab id. */
export const TAB_LABEL_BY_ID: Readonly<Record<string, string>> = {
  [PRICE_CONFIG_TAB]: "Content & pricing",
  [CATEGORY_TAB]: "Shelves",
  [SPECIAL_PROMOTION_TAB]: "Special promotion",
  [PACKAGE_SETTINGS_TAB]: "Package settings",
};

/** Short Thai sub-label shown under each English step label in the stepper.
 *  Helps marketing users orient quickly without reading EN. */
export const TAB_SUBLABEL_TH: Readonly<Record<string, string>> = {
  [PRICE_CONFIG_TAB]: "เนื้อหาและราคา",
  [CATEGORY_TAB]: "ชั้นวางสินค้า",
  [SPECIAL_PROMOTION_TAB]: "โปรโมชันพิเศษ",
  [PACKAGE_SETTINGS_TAB]: "ตั้งค่าแพ็กเกจ",
};

/** Resolve a (possibly aliased) tab id to its canonical id. */
export function normalizeTabId(tabId: string): string {
  return TAB_ALIASES[tabId] ?? tabId;
}

/** Display label for a tab, falling back to the supplied default when the
 *  id is not in TAB_LABEL_BY_ID. */
export function tabDisplayLabel(tabId: string, fallback: string): string {
  return TAB_LABEL_BY_ID[normalizeTabId(tabId)] ?? fallback;
}

function priceCount(model: IEnrichmentModel | null): number {
  if (!model) return 0;
  if (isFlatEnrichmentModel(model)) return model.__detail.prices?.length ?? 0;
  return model.productOfferingPrice?.length ?? 0;
}

/**
 * Compute tab strip items from an enrichment model. Pure — no I/O, no
 * subscriptions. Re-evaluated on every enrichment$ emission, so the
 * Package settings badge follows the price count.
 */
export function computeTabItems(model: IEnrichmentModel | null): TabItem[] {
  const prices = priceCount(model);
  return [
    { id: PRICE_CONFIG_TAB, label: TAB_LABEL_BY_ID[PRICE_CONFIG_TAB] },
    { id: CATEGORY_TAB, label: TAB_LABEL_BY_ID[CATEGORY_TAB] },
    {
      id: SPECIAL_PROMOTION_TAB,
      label: TAB_LABEL_BY_ID[SPECIAL_PROMOTION_TAB],
    },
    {
      id: PACKAGE_SETTINGS_TAB,
      label: TAB_LABEL_BY_ID[PACKAGE_SETTINGS_TAB],
      // Package fields apply to every price; a package with one price has
      // nothing to share, so the mark appears from two prices up.
      ...(prices > 1 ? { badge: `shared by ${prices}` } : {}),
    },
  ];
}

/** Derive stepper status from a TabItem's badge + the section's
 *  publish-readiness status. The readiness map is authoritative when present;
 *  the badge is a fast-path for cases where readiness hasn't computed yet
 *  (e.g. during the initial load).
 *
 *  Marketing-facing semantics:
 *  - `error`        → blocks publish (e.g. missing channels)
 *  - `warning`      → reviewable but allows publish (e.g. partial languages)
 *  - `in-progress`  → has some content but not all required slots are filled
 *  - `complete`     → all required content is filled
 *  - `empty`        → nothing entered yet
 */
export function deriveStepperStatus(
  tab: TabItem,
  readinessStatus?: string,
): StepperStatus {
  // Readiness map wins when available — it knows about cross-section rules
  // (e.g. "channels required" lives in category readiness, not in the tab
  // badge).
  switch (readinessStatus) {
    case "complete":
      return "complete";
    case "warning":
      return "warning";
    case "error":
      return "error";
    case "empty":
      return "empty";
  }

  // Fall back to badge inspection. "!" is the legacy "blocks publish" marker.
  if (tab.badge === "!") return "error";
  if (typeof tab.badge === "number" && tab.badge > 0) return "in-progress";
  return "empty";
}

/** Build a stepper item from a TabItem. Order index is 1-based so it can be
 *  displayed directly as ①②③ — caller should pass the position within the
 *  visible (customer-facing) bucket. */
export function stepperItemFromTab(
  tab: TabItem,
  index: number,
  readinessStatus?: string,
): StepperItem {
  const id = normalizeTabId(tab.id);
  return {
    id,
    label: TAB_LABEL_BY_ID[id] ?? tab.label,
    subLabelTh: TAB_SUBLABEL_TH[id] ?? "",
    status: deriveStepperStatus(tab, readinessStatus),
    badge: tab.badge,
    index,
  };
}

/** Derive a stepper status from a tab's readiness summary. Returns
 *  `undefined` when the summary is missing so the caller can fall back
 *  to its own status logic. */
export function statusFromReadiness(
  readiness: TabReadinessSummary | undefined,
): StepperStatus | undefined {
  if (!readiness) return undefined;
  if (readiness.blockerCount > 0) return "error";
  if (readiness.warningCount > 0) return "warning";
  const hasLangGap = Object.values(readiness.langCoverage).some(
    (c) => c.total > 0 && c.filled < c.total,
  );
  if (hasLangGap) return "in-progress";
  return "complete";
}

/** Convenience — convert a list of TabItems to StepperItems, numbered from 1.
 *  Readiness lookup is optional; the second-arg map keys by tab id and
 *  contributes the authoritative status when present. */
export function stepperItemsFromTabs(
  tabs: readonly TabItem[],
  readinessByTabId?: Readonly<Record<string, string>>,
): StepperItem[] {
  return tabs.map((t, i) =>
    stepperItemFromTab(t, i + 1, readinessByTabId?.[normalizeTabId(t.id)]),
  );
}

/**
 * Build the final stepper items for a tab bucket, resolving each step's status
 * from two layers:
 *
 *  1. The per-tab readiness summary (`tabReadinessMap`) — authoritative when a
 *     tab has blockers / warnings / language gaps. `statusFromReadiness`
 *     returns `error` / `warning` / `in-progress` for these.
 *  2. The section-status fallback (`readinessByTabId`) — a clean tab has NO
 *     entry in the readiness map (the service omits tabs with nothing to
 *     surface), so `statusFromReadiness` returns `undefined`. We then fall
 *     back to the validator's section status, which is the ONLY source that
 *     can report `complete`. Omitting it leaves clean tabs stuck at `empty`,
 *     so the green check never renders even when every section is complete.
 */
export function resolveStepperItems(
  tabs: readonly TabItem[],
  tabReadinessMap: ReadonlyMap<string, TabReadinessSummary>,
  readinessByTabId?: Readonly<Record<string, string>>,
): StepperItem[] {
  return stepperItemsFromTabs(tabs, readinessByTabId).map((s) => {
    const readiness = tabReadinessMap.get(s.id);
    const status = statusFromReadiness(readiness) ?? s.status;
    return { ...s, readiness, status };
  });
}

/** Section statuses from worst to best, for merging two sections' statuses
 *  onto one tab. An unknown status ranks worst, so it is never hidden. */
const STATUS_RANK: readonly string[] = [
  "error",
  "warning",
  "empty",
  "complete",
];

export function worstStatus(a: string | undefined, b: string): string {
  if (a === undefined) return b;
  return STATUS_RANK.indexOf(b) < STATUS_RANK.indexOf(a) ? b : a;
}

export type SaveScope = "price" | "package" | "none";

/** Special promotion edits `prices[i].pricingLogicAlgorithm`, a price-level
 *  field the package PATCH strips, so it is a price tab. */
const SAVE_SCOPE_BY_TAB: Readonly<Record<string, SaveScope>> = {
  [PRICE_CONFIG_TAB]: "price",
  [CATEGORY_TAB]: "price",
  [SPECIAL_PROMOTION_TAB]: "price",
  [PACKAGE_SETTINGS_TAB]: "package",
};

export function scopeOfTab(tabId: string): SaveScope {
  return SAVE_SCOPE_BY_TAB[normalizeTabId(tabId)] ?? "none";
}

/**
 * Whether a tab carries unsaved edits, for the stepper's dirty dot and the
 * SaveBar's `[dirty]` input. The section-dirty tab flag is authoritative:
 * price writers (pricing → `price-config`, shelf → `category`, special
 * promotion → `special-promotion`) always stamp their own tab when they mark
 * a price dirty, so a set flag names the ONE dirty scope. The active-price
 * fallback applies only while the flag is unset (stale-flag safety net) —
 * otherwise a Pricing edit would also light the other price tabs' dots,
 * since all price tabs share the price dirty store.
 */
export function isTabDirty(
  tabId: string,
  dirtyTab: string | null,
  activePriceDirty: boolean,
): boolean {
  if (dirtyTab === tabId) return true;
  return dirtyTab === null && scopeOfTab(tabId) === "price" && activePriceDirty;
}

export interface SavePlanInput {
  readonly tabId: string;
  readonly activePopId: string | null;
  readonly activePriceDirty: boolean;
  readonly dirtyTab: string | null;
}

export type SectionSavePlan =
  | { readonly kind: "noop" }
  | { readonly kind: "price"; readonly priceIds: readonly [string] }
  | { readonly kind: "package" };

const NOOP: SectionSavePlan = { kind: "noop" };

export function planSectionSave(input: SavePlanInput): SectionSavePlan {
  switch (scopeOfTab(input.tabId)) {
    case "price":
      return input.activePopId && input.activePriceDirty
        ? { kind: "price", priceIds: [input.activePopId] }
        : NOOP;
    case "package":
      return input.dirtyTab !== null &&
        normalizeTabId(input.dirtyTab) === normalizeTabId(input.tabId)
        ? { kind: "package" }
        : NOOP;
    default:
      return NOOP;
  }
}
